import { useState } from 'react';

/** Form flow & core data: listing mode, form step, form data, existing input, dimension, validation */
export function useSingleListingFormState(initialNewFormData, initialExistingInput) {
  // Initialize listingMode from URL query param if present (e.g., ?mode=existing)
  const [listingMode, setListingMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    return mode === 'existing' ? 'existing' : 'new';
  });
  const [formData, setFormData] = useState(initialNewFormData);
  const [existingInput, setExistingInput] = useState(initialExistingInput);
  const [dimensionData, setDimensionData] = useState(null);
  const [formStep, setFormStep] = useState(1);
  const [showVariantsTab, setShowVariantsTab] = useState(false);
  const [invalidFields, setInvalidFields] = useState(new Set());
  const [currentAsinValue, setCurrentAsinValue] = useState('');
  const [asinResetKey, setAsinResetKey] = useState(0);
  const [listingUpgradePayload, setListingUpgradePayload] = useState(null);

  return {
    listingMode,
    setListingMode,
    formData,
    setFormData,
    existingInput,
    setExistingInput,
    dimensionData,
    setDimensionData,
    formStep,
    setFormStep,
    showVariantsTab,
    setShowVariantsTab,
    invalidFields,
    setInvalidFields,
    currentAsinValue,
    setCurrentAsinValue,
    asinResetKey,
    setAsinResetKey,
    listingUpgradePayload,
    setListingUpgradePayload
  };
}

/** ASIN lookup & product data from ASIN */
export function useSingleListingAsinState() {
  const [isAsinDataFetched, setIsAsinDataFetched] = useState(false);
  const [asinProductData, setAsinProductData] = useState(null);
  const [isAsinLookupLoading, setIsAsinLookupLoading] = useState(false);
  const [shouldAdvanceAfterAsinFetch, setShouldAdvanceAfterAsinFetch] = useState(false);

  return {
    isAsinDataFetched,
    setIsAsinDataFetched,
    asinProductData,
    setAsinProductData,
    isAsinLookupLoading,
    setIsAsinLookupLoading,
    shouldAdvanceAfterAsinFetch,
    setShouldAdvanceAfterAsinFetch
  };
}

/** Images, sequences (B+/A+), templates, dimension image, carousel, image errors */
export function useSingleListingImageState() {
  const [uploadedImages, setUploadedImages] = useState([]);
  const [bPlusSequence, setBPlusSequence] = useState([]);
  const [aPlusSequence, setAPlusSequence] = useState([]);
  const [zoomAreas, setZoomAreas] = useState([]);
  const [singleImage, setSingleImage] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedTemplatesWithImages, setSelectedTemplatesWithImages] = useState([]);
  const [currentCarouselStep, setCurrentCarouselStep] = useState(1);
  const [singleImageError, setSingleImageError] = useState('');
  const [productIdentityImageError, setProductIdentityImageError] = useState('');
  const [imageError, setImageError] = useState('');
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  const [selectedAPlusTemplate, setSelectedAPlusTemplate] = useState(null);
  const [aplusTemplateImage, setAPlusTemplateImage] = useState(null);
  const [aplusImageError, setAPlusImageError] = useState('');

  return {
    uploadedImages,
    setUploadedImages,
    bPlusSequence,
    setBPlusSequence,
    aPlusSequence,
    setAPlusSequence,
    zoomAreas,
    setZoomAreas,
    singleImage,
    setSingleImage,
    productImages,
    setProductImages,
    selectedTemplates,
    setSelectedTemplates,
    selectedTemplatesWithImages,
    setSelectedTemplatesWithImages,
    currentCarouselStep,
    setCurrentCarouselStep,
    singleImageError,
    setSingleImageError,
    productIdentityImageError,
    setProductIdentityImageError,
    imageError,
    setImageError,
    draggedImageIndex,
    setDraggedImageIndex,
    selectedAPlusTemplate,
    setSelectedAPlusTemplate,
    aplusTemplateImage,
    setAPlusTemplateImage,
    aplusImageError,
    setAPlusImageError
  };
}

/** Draft & navigation blocking */
export function useSingleListingDraftState() {
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [navigationBlocked, setNavigationBlocked] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [isDraftMode, setIsDraftMode] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [targetPath, setTargetPath] = useState(null);

  return {
    showDraftModal,
    setShowDraftModal,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    navigationBlocked,
    setNavigationBlocked,
    currentDraftId,
    setCurrentDraftId,
    isDraftMode,
    setIsDraftMode,
    pendingNavigation,
    setPendingNavigation,
    targetPath,
    setTargetPath
  };
}

/** Submission, loading, result, and validation errors */
export function useSingleListingSubmissionState() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedResult, setGeneratedResult] = useState(null);
  const [showResultPopup, setShowResultPopup] = useState(false);
  const [countError, setCountError] = useState('');
  const [keyFeaturesError, setKeyFeaturesError] = useState(false);

  return {
    isLoading,
    setIsLoading,
    error,
    setError,
    generatedResult,
    setGeneratedResult,
    showResultPopup,
    setShowResultPopup,
    countError,
    setCountError,
    keyFeaturesError,
    setKeyFeaturesError
  };
}

/** Variants tab data and validation */
export function useSingleListingVariantsState() {
  const [variantsDraftData, setVariantsDraftData] = useState(null);
  const [isVariantsValid, setIsVariantsValid] = useState(false);
  const [variantsInternalStep, setVariantsInternalStep] = useState(1);

  return {
    variantsDraftData,
    setVariantsDraftData,
    isVariantsValid,
    setIsVariantsValid,
    variantsInternalStep,
    setVariantsInternalStep
  };
}

/** UI-only state (modals, tutorial, selected theme) */
export function useSingleListingUIState() {
  const [showTutorialStep, setShowTutorialStep] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);

  return {
    showTutorialStep,
    setShowTutorialStep,
    isModalOpen,
    setIsModalOpen,
    selectedTheme,
    setSelectedTheme
  };
}
